// The demo banner (TOM-868, cloud ADR 0419), on every app page while the user
// is a Try it guest. It says until when the copy is theirs, that changes are
// discarded, and where the data comes from, and it offers Join the beta and
// Leave the demo, and Reset demo (TOM-892, cloud ADR 0436). A change the server refuses with `403 demo_read_only` shows
// a short note under it (api.ts sends the event). The guest's own input
// extends the lease through `POST /v1/demo/touch`, at most once a minute, and
// the banner shows the end from the latest answer.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../api';
import { useAuth } from '../../auth';
import {
  DEMO_BANNER_DETAIL,
  DEMO_READ_ONLY_EVENT,
  DEMO_READ_ONLY_NOTE,
  DEMO_RESET_FAILED_NOTE,
  demoBannerLead,
  demoEndsAt,
  demoLeaseEnd,
  demoTouchExpiresAt,
  lastDemoTouchSentMs,
  latestDemoTouch,
  noteDemoTouchAnswer,
  noteDemoTouchSent,
  shouldSendDemoTouch,
} from '../../lib/demo';
import { useLeaveDemo } from '../../lib/useLeaveDemo';
import { useResetDemo } from '../../lib/useResetDemo';

/** How long the refused-change note stays. */
const NOTE_MS = 6_000;

export function DemoBanner() {
  const auth = useAuth();
  const leave = useLeaveDemo();
  const resetDemo = useResetDemo();
  const lease = auth.user?.demo ?? null;
  const [refused, setRefused] = useState(0);
  // The latest touch answer, kept in lib/demo.ts for the page.
  const [touch, setTouch] = useState(latestDemoTouch);

  useEffect(() => {
    const onRefused = () => setRefused((n) => n + 1);
    window.addEventListener(DEMO_READ_ONLY_EVENT, onRefused);
    return () => window.removeEventListener(DEMO_READ_ONLY_EVENT, onRefused);
  }, []);
  useEffect(() => {
    if (refused === 0) return;
    const id = window.setTimeout(() => setRefused(0), NOTE_MS);
    return () => clearTimeout(id);
  }, [refused]);

  // The guest's own input extends the lease, at most once a minute (TOM-868).
  // A touch refused with 401 goes through api(), which reports the ended
  // lease. Any other failure is ignored.
  const meExpiresAt = lease?.expiresAt ?? null;
  useEffect(() => {
    if (meExpiresAt === null) return;
    const onInput = (e: Event) => {
      // Leave the demo ends the lease, and Reset demo replaces the guest. A
      // touch sent with either would only race it.
      if (
        e.target instanceof Element &&
        e.target.closest('[data-demo-leave], [data-demo-reset]') !== null
      ) {
        return;
      }
      const now = Date.now();
      if (!shouldSendDemoTouch(lastDemoTouchSentMs(), now)) return;
      noteDemoTouchSent(now);
      api<unknown>('/v1/demo/touch', { method: 'POST' }).then(
        (body) => {
          const expiresAt = demoTouchExpiresAt(body);
          if (expiresAt === null) return;
          noteDemoTouchAnswer(meExpiresAt, expiresAt);
          setTouch(latestDemoTouch());
        },
        () => undefined
      );
    };
    const opts = { capture: true, passive: true };
    window.addEventListener('pointerdown', onInput, opts);
    window.addEventListener('keydown', onInput, opts);
    return () => {
      window.removeEventListener('pointerdown', onInput, opts);
      window.removeEventListener('keydown', onInput, opts);
    };
  }, [meExpiresAt]);

  if (meExpiresAt === null) return null;

  return (
    <DemoBannerView
      expiresAt={demoLeaseEnd(meExpiresAt, touch)}
      refused={refused > 0}
      onLeave={() => void leave()}
      onReset={() => void resetDemo.reset()}
      resetting={resetDemo.busy}
      resetFailed={resetDemo.failed}
    />
  );
}

/** The banner's markup, apart from the session, for tests. */
export function DemoBannerView({
  expiresAt,
  refused,
  onLeave,
  onReset,
  resetting = false,
  resetFailed = false,
  nowMs = Date.now(),
}: {
  expiresAt: string;
  refused: boolean;
  onLeave: () => void;
  onReset: () => void;
  resetting?: boolean;
  resetFailed?: boolean;
  nowMs?: number;
}) {
  return (
    <div className="demo-banner" role="region" aria-label="Demo" data-testid="demo-banner">
      <p className="demo-banner__text">
        <b>{demoBannerLead(demoEndsAt(expiresAt, nowMs))}</b>{' '}
        <span className="demo-banner__detail">{DEMO_BANNER_DETAIL}</span>
      </p>
      <div className="demo-banner__actions">
        <Link to="/beta" className="tb-btn tb-btn--sm" data-testid="demo-join-beta">
          Join the beta
        </Link>
        <button
          type="button"
          className="tb-btn tb-btn--sm tb-btn--gray"
          data-testid="demo-reset"
          data-demo-reset=""
          disabled={resetting}
          onClick={onReset}
        >
          {resetting ? 'Resetting…' : 'Reset demo'}
        </button>
        <button
          type="button"
          className="tb-btn tb-btn--sm tb-btn--gray"
          data-testid="demo-leave"
          data-demo-leave=""
          onClick={onLeave}
        >
          Leave the demo
        </button>
      </div>
      {resetFailed ? (
        <p className="demo-banner__note" role="status" data-testid="demo-reset-failed">
          {DEMO_RESET_FAILED_NOTE}
        </p>
      ) : null}
      {refused ? (
        <p className="demo-banner__note" role="status" data-testid="demo-read-only">
          {DEMO_READ_ONLY_NOTE}
        </p>
      ) : null}
    </div>
  );
}
